// frontend/src/pages/LoginPage.tsx
import React, { useState } from 'react';
import { LoginButton } from '@telegram-auth/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ApiService } from '../services/api';
import { trackGoal } from '../utils/metrika';

// Предполагается, что имя вашего Telegram бота будет храниться в переменной окружения
const TELEGRAM_BOT_NAME = process.env.REACT_APP_TELEGRAM_BOT_NAME || 'YOUR_TELEGRAM_BOT_NAME_PLACEHOLDER';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // State для email авторизации
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const redirectPath = '/my-applications';

  const handleTelegramResponse = (response: any) => {
    console.log('Telegram auth response:', response);
    trackGoal('login_click', { source: 'login_page', method: 'telegram' });
    // Создаем URL с параметрами Telegram аутентификации
    const params = new URLSearchParams();
    
    // Добавляем все данные от Telegram
    Object.entries(response).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });
    
    // Добавляем параметр next для редиректа БЕЗ дополнительного кодирования
    params.append('next', window.location.origin + redirectPath);
    
    // Перенаправляем на backend callback с параметрами
    window.location.href = `${API_BASE_URL}/api/auth/telegram-callback?${params.toString()}`;
  };

  // Формируем URL для Telegram коллбэка БЕЗ двойного кодирования
  const backendCallbackUrl = `${API_BASE_URL}/api/auth/telegram-callback?next=${window.location.origin + redirectPath}`;

  // Валидация email
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Обработка ввода email и проверка существования
  const handleEmailContinue = async () => {
    if (!validateEmail(email)) {
      setError('Пожалуйста, введите корректный email адрес');
      return;
    }

    trackGoal('login_click', { source: 'login_page', method: 'email_continue' });
    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      const result = await ApiService.checkEmailExists(email);
      
      if (result.exists && result.requirePassword) {
        // Пользователь существует - показываем поле пароля
        setShowPasswordField(true);
        setMessage('Введите ваш пароль');
      } else {
        // Новый пользователь - сразу регистрируем
        await handleEmailRegister();
      }
    } catch (error) {
      setError('Ошибка при проверке email. Попробуйте позже.');
    } finally {
      setIsLoading(false);
    }
  };

  // Регистрация нового пользователя
  const handleEmailRegister = async () => {
    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('next_url', window.location.origin + redirectPath);
      
      // Используем fetch с redirect: 'manual' чтобы обработать редирект самостоятельно
      const response = await fetch(`${API_BASE_URL}/api/auth/email-register`, {
        method: 'POST',
        body: formData,
        credentials: 'include', // Важно для передачи cookie
        redirect: 'manual' // Обрабатываем редирект вручную
      });
      
      if (response.type === 'opaqueredirect' || response.status === 302 || response.status === 307) {
        // Успешная регистрация, перенаправляем на нужную страницу
        window.location.href = redirectPath;
      } else {
        setError('Ошибка при регистрации. Попробуйте позже.');
      }
    } catch (error) {
      setError('Ошибка при регистрации. Попробуйте позже.');
    }
  };

  // Вход существующего пользователя
  const handleEmailLogin = async () => {
    if (!password.trim()) {
      setError('Пожалуйста, введите пароль');
      return;
    }

    trackGoal('login_click', { source: 'login_page', method: 'email_password' });
    setIsLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('password', password);
      formData.append('next_url', window.location.origin + redirectPath);
      
      // Используем fetch с redirect: 'manual' чтобы обработать редирект самостоятельно
      const response = await fetch(`${API_BASE_URL}/api/auth/email-login`, {
        method: 'POST',
        body: formData,
        credentials: 'include', // Важно для передачи cookie
        redirect: 'manual' // Обрабатываем редирект вручную
      });
      
      if (response.type === 'opaqueredirect' || response.status === 302 || response.status === 307) {
        // Успешный вход, перенаправляем на нужную страницу
        window.location.href = redirectPath;
      } else {
        setError('Неверный email или пароль');
        setIsLoading(false);
      }
    } catch (error) {
      setError('Неверный email или пароль');
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (!showPasswordField) {
        handleEmailContinue();
      } else {
        handleEmailLogin();
      }
    }
  };

  const resetEmailForm = () => {
    setShowPasswordField(false);
    setPassword('');
    setMessage('');
    setError('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <h1 className="text-2xl font-bold mb-6 text-center">Вход в систему</h1>
        
        {/* Telegram авторизация */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4 text-center">Войти через Telegram</h2>
          <div className="flex justify-center">
            <LoginButton
              botUsername={TELEGRAM_BOT_NAME}
              onAuthCallback={handleTelegramResponse}
              buttonSize="large"
              cornerRadius={5}
              showAvatar={true}
              lang="ru"
            />
          </div>
        </div>

        {/* Разделитель */}
        <div className="flex items-center mb-8">
          <div className="flex-grow border-t border-gray-300"></div>
          <span className="px-4 text-gray-500 text-sm">или</span>
          <div className="flex-grow border-t border-gray-300"></div>
        </div>

        {/* Email авторизация */}
        <div>
          <h2 className="text-lg font-semibold mb-4 text-center">Войти по email</h2>
          
          {/* Поле email */}
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email адрес
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="example@domain.com"
              disabled={isLoading || showPasswordField}
            />
          </div>

          {/* Поле пароля (показывается только для существующих пользователей) */}
          {showPasswordField && (
            <div className="mb-4">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Пароль
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Введите ваш пароль"
                disabled={isLoading}
                autoFocus
              />
            </div>
          )}

          {/* Сообщения */}
          {message && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-blue-700 text-sm">{message}</p>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Кнопки */}
          <div className="space-y-3">
            {!showPasswordField ? (
              <button
                onClick={handleEmailContinue}
                disabled={isLoading || !email.trim()}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Проверяем...' : 'Продолжить'}
              </button>
            ) : (
              <>
                <button
                  onClick={handleEmailLogin}
                  disabled={isLoading || !password.trim()}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Входим...' : 'Войти'}
                </button>
                <button
                  onClick={resetEmailForm}
                  disabled={isLoading}
                  className="w-full bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Изменить email
                </button>
              </>
            )}
          </div>
        </div>

        <p className="mt-6 text-sm text-gray-500 text-center">
          Все данные строго конфиденциальны и анонимны.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
